import { cloneElement, useEffect, useMemo, useRef, useState } from "react"

// --- Hooks ---
import { useIsBreakpoint } from "@/tiptap/hooks/use-is-breakpoint"
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"

// --- Tiptap UI ---
import {
  ColorHighlightPopover,
  ColorHighlightPopoverButton,
  ColorHighlightPopoverContent,
} from "@/tiptap/ui/color-highlight-popover"
import { ImageUploadButton } from "@/tiptap/ui/image-upload-button"
import {
  canSetLink,
  LinkButton,
  LinkContent,
  LinkPopover,
} from "@/tiptap/ui/link-popover"
import { MarkButton } from "@/tiptap/ui/mark-button"
import { TextAlignButton } from "@/tiptap/ui/text-align-button"
import { SlashCommandTriggerButton } from "@/tiptap/ui/slash-command-trigger-button"
import { ResetAllFormattingButton } from "@/tiptap/ui/reset-all-formatting-button"
import { DeleteNodeButton } from "@/tiptap/ui/delete-node-button"
// [AI DISABLED] import { ImproveDropdown } from "@/tiptap/ui/improve-dropdown"
import { CopyAnchorLinkButton } from "@/tiptap/ui/copy-anchor-link-button"
import { TurnIntoDropdownContent } from "@/tiptap/ui/turn-into-dropdown"
import { useRecentColors } from "@/tiptap/ui/color-text-popover"
import {
  ColorTextButton,
  TEXT_COLORS,
} from "@/tiptap/ui/color-text-button"
import {
  canColorHighlight,
  ColorHighlightButton,
  HIGHLIGHT_COLORS,
} from "@/tiptap/ui/color-highlight-button"
// [AI DISABLED] import { AiAskButton } from "@/tiptap/ui/ai-ask-button"
import { DuplicateButton } from "@/tiptap/ui/duplicate-button"
import { CopyToClipboardButton } from "@/tiptap/ui/copy-to-clipboard-button"
import { IndentButton } from "@/tiptap/ui/indent-button"

// --- Utils ---
import { getNodeDisplayName } from "@/tiptap/lib/tiptap-collab-utils"

// --- Icons ---
import { PaintBucketIcon } from "@/tiptap/icons/paint-bucket-icon"
import { Repeat2Icon } from "@/tiptap/icons/repeat-2-icon"

// --- UI Primitives ---
import { Spacer } from "@/tiptap/ui-primitive/spacer"
import { Separator } from "@/tiptap/ui-primitive/separator"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/tiptap/ui-primitive/dropdown-menu"
import { ArrowLeftIcon } from "@/tiptap/icons/arrow-left-icon"
import { ChevronRightIcon } from "@/tiptap/icons/chevron-right-icon"
import { HighlighterIcon } from "@/tiptap/icons/highlighter-icon"
import { LinkIcon } from "@/tiptap/icons/link-icon"
import { MoreVerticalIcon } from "@/tiptap/icons/more-vertical-icon"
import { Button } from "@/tiptap/ui-primitive/button"
import {
  Toolbar,
  ToolbarGroup,
  ToolbarSeparator,
} from "@/tiptap/ui-primitive/toolbar"
import { MoveNodeButton } from "@/tiptap/ui/move-node-button"
import { ImageNodeFloating } from "@/tiptap/nodes/image-node/image-node-floating"

// =============================================================================
// Types & Constants
// =============================================================================

const TOOLBAR_VIEWS = {
  MAIN: "main",
  HIGHLIGHTER: "highlighter",
  LINK: "link"
}

const MOBILE_TOOLBAR_BREAKPOINT = 768

// =============================================================================
// Hooks
// =============================================================================

function useToolbarState(isMobile) {
  const [viewId, setViewId] = useState(TOOLBAR_VIEWS.MAIN)

  useEffect(() => {
    if (!isMobile && viewId !== TOOLBAR_VIEWS.MAIN) {
      setViewId(TOOLBAR_VIEWS.MAIN)
    }
  }, [isMobile, viewId])

  return {
    viewId,
    setViewId,
    isMainView: viewId === TOOLBAR_VIEWS.MAIN,
    showMainView: () => setViewId(TOOLBAR_VIEWS.MAIN),
    showView: (id) => setViewId(id),
  };
}

function hasTextSelection(editor) {
  if (!editor || !editor.isEditable) return false

  const { selection } = editor.state
  return !selection.empty
}

// =============================================================================
// Toolbar View Registry
// =============================================================================

function createToolbarViewRegistry() {
  return {
    [TOOLBAR_VIEWS.HIGHLIGHTER]: {
      id: TOOLBAR_VIEWS.HIGHLIGHTER,
      title: "Text Highlighter",
      icon: <HighlighterIcon className="tiptap-button-icon" />,
      content: <ColorHighlightPopoverContent />,
      mobileButton: (onClick) => (
        <ColorHighlightPopoverButton onClick={onClick} />
      ),
      desktopComponent: <ColorHighlightPopover />,
      shouldShow(editor) {
        return canColorHighlight(editor);
      },
    },
    [TOOLBAR_VIEWS.LINK]: {
      id: TOOLBAR_VIEWS.LINK,
      title: "Link Editor",
      icon: <LinkIcon className="tiptap-button-icon" />,
      content: <LinkContent />,
      mobileButton: (onClick) => <LinkButton onClick={onClick} />,
      desktopComponent: <LinkPopover />,
      shouldShow(editor) {
        return canSetLink(editor);
      },
    },
  };
}

// =============================================================================
// Sub-Components
// =============================================================================

function IndentGroup() {
  return (
    <>
      <ToolbarGroup>
        <IndentButton action="outdent" hideWhenUnavailable />
        <IndentButton action="indent" hideWhenUnavailable />
      </ToolbarGroup>
      <ToolbarSeparator />
    </>
  );
}

function AlignmentGroup() {
  return (
    <>
      <ToolbarGroup>
        <TextAlignButton align="left" hideWhenUnavailable />
        <TextAlignButton align="center" hideWhenUnavailable />
        <TextAlignButton align="right" hideWhenUnavailable />
        <TextAlignButton align="justify" hideWhenUnavailable />
      </ToolbarGroup>
      <ToolbarSeparator />
    </>
  );
}

function ScriptGroup() {
  return (
    <>
      <ToolbarGroup>
        <MarkButton type="superscript" hideWhenUnavailable />
        <MarkButton type="subscript" hideWhenUnavailable />
      </ToolbarGroup>
      <ToolbarSeparator />
    </>
  );
}

function FormattingGroup() {
  return (
    <>
      <ToolbarGroup>
        <MarkButton type="bold" hideWhenUnavailable />
        <MarkButton type="italic" hideWhenUnavailable />
        <MarkButton type="underline" hideWhenUnavailable />
        <MarkButton type="strike" hideWhenUnavailable />
        <MarkButton type="code" hideWhenUnavailable />
      </ToolbarGroup>
      <ToolbarSeparator />
    </>
  );
}

function ColorActionGroup() {
  const { recentColors, isInitialized, addRecentColor } = useRecentColors()

  const renderRecentColors = () => {
    if (!isInitialized || recentColors.length === 0) return null

    return (
      <>
        <DropdownMenuGroup>
          <DropdownMenuLabel>Recent colors</DropdownMenuLabel>
          {recentColors.map((colorObj) => (
            <DropdownMenuItem key={`${colorObj.type}-${colorObj.value}`} asChild>
              {colorObj.type === "text" ? (
                <ColorTextButton
                  textColor={colorObj.value}
                  label={colorObj.label}
                  text={colorObj.label}
                  tooltip={colorObj.label}
                  onApplied={({ color, label }) =>
                    addRecentColor({
                      type: "text",
                      label,
                      value: color,
                    })
                  } />
              ) : (
                <ColorHighlightButton
                  highlightColor={colorObj.value}
                  text={colorObj.label}
                  tooltip={colorObj.label}
                  onApplied={({ color, label }) =>
                    addRecentColor({
                      type: "highlight",
                      label,
                      value: color,
                    })
                  } />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
        <Separator orientation="horizontal" />
      </>
    );
  }

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger asChild>
        <Button variant="ghost">
          <PaintBucketIcon className="tiptap-button-icon" />
          <span className="tiptap-button-text">Color</span>
          <Spacer />
          <ChevronRightIcon className="tiptap-button-icon" />
        </Button>
      </DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent>
          {renderRecentColors()}

          <DropdownMenuGroup>
            <DropdownMenuLabel>Text color</DropdownMenuLabel>

            {TEXT_COLORS.map((textColor) => (
              <DropdownMenuItem key={textColor.value} asChild>
                <ColorTextButton
                  textColor={textColor.value}
                  label={textColor.label}
                  text={textColor.label}
                  tooltip={textColor.label}
                  onApplied={({ color, label }) =>
                    addRecentColor({ type: "text", label, value: color })
                  } />
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>

          <Separator orientation="horizontal" />

          <DropdownMenuGroup>
            <DropdownMenuLabel>Highlight color</DropdownMenuLabel>

            {HIGHLIGHT_COLORS.map((highlightColor) => (
              <DropdownMenuItem key={highlightColor.value} asChild>
                <ColorHighlightButton
                  highlightColor={highlightColor.value}
                  text={highlightColor.label}
                  tooltip={highlightColor.label}
                  onApplied={({ color, label }) =>
                    addRecentColor({
                      type: "highlight",
                      label,
                      value: color,
                    })
                  } />
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
}

function TransformActionGroup({ editor }) {
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger asChild>
        <Button variant="ghost">
          <Repeat2Icon className="tiptap-button-icon" />
          <span className="tiptap-button-text">Turn into</span>
          <Spacer />
          <ChevronRightIcon className="tiptap-button-icon" />
        </Button>
      </DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent>
          <TurnIntoDropdownContent editor={editor} />
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
}

function DropdownMenuActions({
  editor
}) {
  const isMobile = useIsBreakpoint()

  return (
    <>
      <DropdownMenuGroup>
        <DropdownMenuLabel>{getNodeDisplayName(editor)}</DropdownMenuLabel>

        <ColorActionGroup />
        <TransformActionGroup editor={editor} />

        <DropdownMenuItem asChild>
          <ResetAllFormattingButton text="Reset formatting" />
        </DropdownMenuItem>
      </DropdownMenuGroup>
      <Separator orientation="horizontal" />
      <DropdownMenuGroup>
        <DropdownMenuItem asChild>
          <DuplicateButton text="Duplicate node" showShortcut={!isMobile} />
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <CopyToClipboardButton text="Copy to clipboard" showShortcut={!isMobile} />
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <CopyAnchorLinkButton text="Copy anchor link" showShortcut={!isMobile} />
        </DropdownMenuItem>
      </DropdownMenuGroup>
      <Separator orientation="horizontal" />
      <DropdownMenuGroup>
        {/* [AI DISABLED] <DropdownMenuItem asChild><AiAskButton text="Ask AI" showShortcut={!isMobile} /></DropdownMenuItem> */}
      </DropdownMenuGroup>
      <Separator orientation="horizontal" />
      <DropdownMenuGroup>
        <DropdownMenuItem asChild>
          <DeleteNodeButton text="Delete" showShortcut={!isMobile} />
        </DropdownMenuItem>
      </DropdownMenuGroup>
    </>
  );
}

function MoreActionsDropdown({
  editor
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" data-appearance="subdued">
          <MoreVerticalIcon className="tiptap-button-icon" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuActions editor={editor} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ToolbarViewButton({
  view,
  isMobile,
  onViewChange
}) {
  const viewId = view.id

  if (isMobile) {
    return view.mobileButton ? (
      cloneElement(view.mobileButton(() => onViewChange(viewId)), { key: view.id })
    ) : (
      <Button key={view.id} onClick={() => onViewChange(viewId)}>
        {view.icon}
      </Button>
    );
  }

  return view.desktopComponent
    ? cloneElement(view.desktopComponent, {
        key: view.id,
      })
    : null;
}

function ToolbarViewsGroup({
  toolbarViews,
  isMobile,
  onViewChange,
  editor
}) {
  const visibleViews = Object.values(toolbarViews).filter((view) => {
    if (!view.shouldShow) return true
    return view.shouldShow(editor);
  })

  if (visibleViews.length === 0) return null

  return (
    <>
      {visibleViews.map((view) => (
        <ToolbarViewButton key={view.id} view={view} isMobile={isMobile} onViewChange={onViewChange} />
      ))}
      <ToolbarSeparator />
    </>
  );
}

function MainToolbarContent({
  editor,
  isMobile,
  toolbarViews,
  onViewChange
}) {
  const hasSelection = hasTextSelection(editor)
  const hasContent = (editor?.getText().length ?? 0) > 0

  return (
    <>
      <ToolbarGroup>
        <SlashCommandTriggerButton />
        <MoreActionsDropdown editor={editor} />

        <ToolbarSeparator />
      </ToolbarGroup>
      {(hasSelection || hasContent) && (
        <>
          {/* [AI DISABLED] <ToolbarGroup><ImproveDropdown hideWhenUnavailable /></ToolbarGroup><ToolbarSeparator /> */}

          <FormattingGroup />

          <ToolbarViewsGroup
            toolbarViews={toolbarViews}
            isMobile={isMobile}
            onViewChange={onViewChange}
            editor={editor} />

          <ImageNodeFloating />

          <ScriptGroup />

          <AlignmentGroup />

          <IndentGroup />

          <ToolbarGroup>
            <ImageUploadButton text="Add" />
            <ToolbarSeparator />
          </ToolbarGroup>
        </>
      )}
      <ToolbarGroup>
        <MoveNodeButton direction="down" />
        <MoveNodeButton direction="up" />
      </ToolbarGroup>
    </>
  );
}

function SpecializedToolbarContent({
  view,
  onBack
}) {
  return (
    <>
      <ToolbarGroup>
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeftIcon className="tiptap-button-icon" />
          {view.icon}
        </Button>
      </ToolbarGroup>
      <ToolbarSeparator />
      {view.content}
    </>
  );
}

export function MobileToolbar({
  editor: providedEditor
}) {
  const { editor } = useTiptapEditor(providedEditor)
  const isMobile = useIsBreakpoint("max", MOBILE_TOOLBAR_BREAKPOINT)
  const toolbarRef = useRef(null)
  const toolbarState = useToolbarState(isMobile)
  const toolbarViews = useMemo(() => createToolbarViewRegistry(), [])

  const currentView = toolbarState.isMainView
    ? null
    : toolbarViews[
        toolbarState.viewId
      ]

  if (!isMobile || !editor || !editor.isEditable) {
    return null
  }

  return (
    <Toolbar
      ref={toolbarRef}
      data-mobile-toolbar="true">
      {toolbarState.isMainView ? (
        <MainToolbarContent
          editor={editor}
          isMobile={isMobile}
          toolbarViews={toolbarViews}
          onViewChange={toolbarState.showView} />
      ) : (
        currentView && (
          <SpecializedToolbarContent view={currentView} onBack={toolbarState.showMainView} />
        )
      )}
    </Toolbar>
  );
}
